/**
 * ActivityTableWidget
 *
 * Dashboard widget that displays recent transactions.
 * Shows contributions, gifts, and transfers in a compact table.
 * Self-contained - fetches its own data.
 */
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { format } from 'date-fns'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
} from '@mui/material'

import { queries } from '../../../graphql'
import { Amount } from '../../amount/amount'

function getStateColor(state) {
  const colors = {
    completed: 'success',
    cleared: 'success',
    sent: 'success',
    pending: 'warning',
    initiated: 'info',
    processing: 'info',
    failed: 'error',
    canceled: 'default',
  }
  return colors[state?.toLowerCase()] || 'default'
}

export function ActivityTableWidget({ fundId: propFundId, limit = 10, showType = true }) {
  const { fundId: paramFundId } = useParams()
  const fundId = propFundId || paramFundId

  const { data, loading, error } = useQuery(queries.funds.myFundActivity, {
    skip: !fundId,
    variables: { fundId: parseInt(fundId, 10) },
  })

  const fund = data?.me?.fund

  // Combine and sort all activities
  const activities = useMemo(() => {
    if (!fund) return []

    const items = []

    // Add contributions
    fund.contributions?.forEach((c) => {
      items.push({
        id: `contribution-${c.id}`,
        type: 'Contribution',
        description: c.description || `${c.paymentType} contribution`,
        amount: c.netAmount || c.amount,
        date: new Date(c.createdAt),
        state: c.state,
        isPositive: true,
      })
    })

    // Add grant requests (gifts)
    fund.grantRequests?.forEach((g) => {
      items.push({
        id: `grant-${g.id}`,
        type: 'Gift',
        description: g.charity?.accountName || g.description || 'Gift to charity',
        amount: g.netAmount || g.amount,
        date: new Date(g.createdAt),
        state: g.state,
        isPositive: false,
      })
    })

    // Add DAF transfers
    fund.dafTransfers?.forEach((d) => {
      const isIncoming = d.receivingFundId === fund.id
      items.push({
        id: `daf-${d.id}`,
        type: isIncoming ? 'Transfer In' : 'Transfer Out',
        description: d.description || (isIncoming ? 'Incoming transfer' : 'Outgoing transfer'),
        amount: d.netAmount || d.amount,
        date: new Date(d.createdAt),
        state: d.state,
        isPositive: isIncoming,
      })
    })

    // Sort by date descending and limit
    return items.sort((a, b) => b.date - a.date).slice(0, limit)
  }, [fund, limit])

  if (loading) {
    return <LinearProgress />
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Error loading activity: {error.message}</Typography>
      </Box>
    )
  }

  if (!fund) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No activity data available</Typography>
      </Box>
    )
  }

  if (activities.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">No recent activity</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Recent Activity
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              {showType && <TableCell>Type</TableCell>}
              <TableCell>Description</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activities.map((activity) => (
              <TableRow key={activity.id} hover>
                <TableCell>{format(activity.date, 'MMM d, yyyy')}</TableCell>
                {showType && (
                  <TableCell>
                    <Chip label={activity.type} size="small" variant="outlined" />
                  </TableCell>
                )}
                <TableCell>{activity.description}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ color: activity.isPositive ? 'success.main' : 'text.primary' }}>
                    {activity.isPositive ? '+' : '-'}
                    <Amount value={activity.amount || 0} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={activity.state} size="small" color={getStateColor(activity.state)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
